/**
 * Insurance checkout screen logic
 *
 * Shared state, derived data, effects, and handlers for the three insurance checkout screens
 * (`invoice`, `without_abtretung_patient`, `without_abtretung_physiofit`). The screens differ
 * only in the per-variant config (`insurance/insuranceCheckoutVariants.ts`) and in the
 * billing-detail drawer they render; everything else lives here and in `InsuranceCheckoutLayout`.
 *
 * References:
 * - `insurance/insuranceCheckoutVariants.ts` — per-variant step key, consent copy, disclaimer
 * - `components/content/InsuranceCheckoutLayout.tsx` — shared presentation
 */

import {
  PATIENT_TRACKING_EVENTS,
  RecordPatientTermsAcceptanceRequest,
  RecordPatientTermsAcceptanceResponse,
} from '@physiofit-digital/physiofit_types';
import { usePostHog } from '@posthog/react';
import { httpsCallable } from 'firebase/functions';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { functions } from '../../../firebaseConfig';
import { PHYSIOFIT_PRIVACY_NOTICE_TERM } from '../insurance/consentTerms';
import {
  INSURANCE_CHECKOUT_VARIANTS,
  type InsuranceCheckoutVariant,
} from '../insurance/insuranceCheckoutVariants';
import { displayInsuranceName, displayInsuranceShortName } from '../insurance/displayInsuranceName';
import {
  ONBOARDING_COURSE_FEE_FALLBACK_EUR,
  resolveCourseFeeEur,
  resolveReimbursementEur,
} from '../insurance/metaBuilders';
import { getInsuranceExplainPath } from '../insurance/routing';
import { ONBOARDING_APP_VERSION_LABEL } from '../integrations/posthog/telemetry';
import { persistDisplayedBillingInfo, useSaveOnboardingStep } from '../services/onboardingProgress';
import { getSignedUpPatient, useOnboardingStore } from '../store/onboardingStore';
import { useCourseZppInfo } from './useCourseZppInfo';
import { useInsuranceExplainBillingGuard } from './useInsuranceExplainBillingGuard';
import { usePatientAppLegal } from './usePatientAppLegal';
import { useSelectedInsurance } from './useSelectedInsurance';

type PatientTermAcceptanceType = 'billing' | 'liability' | 'other';

export type PatientTerm = {
  type: PatientTermAcceptanceType;
  checkboxText: string;
  title: string;
  infoUrl?: string;
  infoTitle?: string;
  infoText?: string;
};

export function useInsuranceCheckoutScreen(variant: InsuranceCheckoutVariant) {
  const config = INSURANCE_CHECKOUT_VARIANTS[variant];
  useSaveOnboardingStep(config.onboardingStep);
  const posthog = usePostHog();
  const navigate = useNavigate();
  const signedUpPatient = useOnboardingStore(getSignedUpPatient);
  const { insurance, loading: insuranceLoading, error: insuranceError } = useSelectedInsurance();
  const legalResult = usePatientAppLegal();

  const [reimbursementAccepted, setReimbursementAccepted] = useState(false);
  const [liabilityAccepted, setLiabilityAccepted] = useState(false);
  const [isBillingDetailOpen, setIsBillingDetailOpen] = useState(false);
  const [isLiabilityDrawerOpen, setIsLiabilityDrawerOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [consentError, setConsentError] = useState(false);
  const consentSectionRef = useRef<HTMLDivElement>(null);
  const displayedBillingPersistedRef = useRef(false);

  const allConsentsAccepted = reimbursementAccepted && liabilityAccepted;

  const contentCourseId = signedUpPatient?.course?.id?.trim() ?? '';
  // Only BNPL courses run under the insurer price; these checkouts are never BNPL, so their course is certified at the
  // platform price (ERS-113).
  const courseInfo = useCourseZppInfo(contentCourseId, ONBOARDING_COURSE_FEE_FALLBACK_EUR);

  const insuranceName = useMemo(() => {
    if (insurance) {
      return displayInsuranceName(insurance);
    }
    return signedUpPatient?.webAppOnboarding?.insuranceInfo?.name?.trim() ?? '';
  }, [insurance, signedUpPatient?.webAppOnboarding?.insuranceInfo?.name]);

  const insuranceShortName = useMemo(() => {
    if (insurance) {
      return displayInsuranceShortName(insurance) || insuranceName;
    }
    return insuranceName;
  }, [insurance, insuranceName]);

  const designation = useMemo(() => {
    if (courseInfo.status === 'ready' && courseInfo.designation) {
      return courseInfo.designation;
    }
    return signedUpPatient?.course?.name?.trim() ?? '';
  }, [courseInfo, signedUpPatient?.course?.name]);

  const billingMath = useMemo(() => {
    if (!insurance) {
      return null;
    }
    const refundRate = insurance.refundRate;
    if (refundRate == null || Number.isNaN(refundRate)) {
      return null;
    }
    const courseAmount = resolveCourseFeeEur(insurance);
    const reimbursement = resolveReimbursementEur(insurance, courseAmount);
    const subsidy = courseAmount - reimbursement;
    return { refundRate, courseAmount, reimbursement, subsidy };
  }, [insurance]);

  useInsuranceExplainBillingGuard(variant);

  useEffect(() => {
    if (!insurance || !billingMath || displayedBillingPersistedRef.current) {
      return;
    }
    displayedBillingPersistedRef.current = true;
    void persistDisplayedBillingInfo(insurance);
  }, [insurance, billingMath]);

  // Surfaces only after a blocked forward attempt, and clears itself once every box is checked.
  const showConsentError = consentError && !allConsentsAccepted;

  const handleBack = useCallback(() => {
    navigate(getInsuranceExplainPath(variant));
  }, [navigate, variant]);

  const handleOpenBillingDetail = useCallback(() => {
    setIsBillingDetailOpen(true);
    posthog.capture(PATIENT_TRACKING_EVENTS.web_insurance_invoice_details, {
      insuranceId: insurance?.id,
      billingType: signedUpPatient?.webAppOnboarding?.insuranceInfo?.billingType,
    });
  }, [posthog, insurance?.id, signedUpPatient?.webAppOnboarding?.insuranceInfo?.billingType]);

  const disclaimerText = useMemo(() => {
    if (!billingMath) return '';
    return config.buildDisclaimerText(billingMath, { insuranceName, insuranceShortName });
  }, [config, billingMath, insuranceName, insuranceShortName]);

  /** The boxes the patient actually ticks, in render order. */
  const consentTerms = useMemo<PatientTerm[]>(
    () => [
      {
        type: 'billing',
        checkboxText: config.billingCheckboxText,
        title: 'Erstattungsabwicklung',
        infoText: disclaimerText,
      },
      {
        type: 'liability',
        checkboxText:
          'Ich bestätige den Haftungsausschluss und dass ich an keinen behandlungsbedürftigen Erkrankungen des Bewegungsapparats leide.',
        title: 'Haftungsausschluss',
        infoTitle:
          legalResult.status === 'ready'
            ? legalResult.legal.liabilityTitle || 'Haftungsausschluss & Kontraindikatoren'
            : 'Haftungsausschluss & Kontraindikatoren',
        infoText: legalResult.status === 'ready' ? legalResult.legal.liabilityText || '' : '',
      },
    ],
    [legalResult, disclaimerText, config.billingCheckboxText],
  );

  const handleForward = useCallback(async () => {
    if (!allConsentsAccepted) {
      setConsentError(true);
      consentSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    setIsSubmitting(true);
    try {
      const acceptedDate = new Date().toISOString();
      await httpsCallable<RecordPatientTermsAcceptanceRequest, RecordPatientTermsAcceptanceResponse>(
        functions,
        'recordPatientTermsAcceptance',
      )({
        userVariant: 'withTherapist',
        personalInfo: {
          firstName: signedUpPatient?.patientFirstName ?? '',
          lastName: signedUpPatient?.patientLastName ?? '',
          phoneNumber: signedUpPatient?.patientPhone ?? '',
        },
        signedUpPatientId: signedUpPatient?.id,
        courseZppName: signedUpPatient?.course?.name,
        billingType: signedUpPatient?.webAppOnboarding?.insuranceInfo?.billingType,
        termsData: [PHYSIOFIT_PRIVACY_NOTICE_TERM, ...consentTerms].map((term) => ({ ...term, acceptedDate })),
        deviceInfo: {
          platform: typeof navigator !== 'undefined' ? navigator.platform : 'unknown',
          systemVersion: typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown',
          appVersion: ONBOARDING_APP_VERSION_LABEL,
        },
      });
      posthog.capture(PATIENT_TRACKING_EVENTS.web_insurance_invoice_checkout_completed, {
        billingType: signedUpPatient?.webAppOnboarding?.insuranceInfo?.billingType,
      });
      navigate('/webAppOnboarding/install');
    } catch (error) {
      setIsSubmitting(false);
      throw error;
    }
  }, [navigate, signedUpPatient, consentTerms, posthog, allConsentsAccepted]);

  const legalLoading = legalResult.status === 'loading';
  const legalReady = legalResult.status === 'ready';
  // Data-not-ready states truly disable the button (unclickable). Missing consents instead
  // mark it inactive: dimmed but clickable, so `handleForward` can scroll + show the notice.
  const forwardDisabled = legalLoading || !legalReady || !billingMath || isSubmitting;
  const forwardInactive = !allConsentsAccepted;

  return {
    insurance,
    insuranceLoading,
    insuranceError,
    legalResult,
    insuranceName,
    insuranceShortName,
    designation,
    billingMath,
    courseInfo,
    contentCourseId,
    disclaimerText,
    consentTerms,
    reimbursementAccepted,
    setReimbursementAccepted,
    liabilityAccepted,
    setLiabilityAccepted,
    consentError: showConsentError,
    consentSectionRef,
    isBillingDetailOpen,
    setIsBillingDetailOpen,
    isLiabilityDrawerOpen,
    setIsLiabilityDrawerOpen,
    isSubmitting,
    forwardDisabled,
    forwardInactive,
    handleBack,
    handleForward,
    handleOpenBillingDetail,
  };
}

export type UseInsuranceCheckoutScreenResult = ReturnType<typeof useInsuranceCheckoutScreen>;
